import { useEffect } from 'react';
import { logic } from '../../../logic';
import { OrderAssistFormData, AddressWithFull } from '../../../types';
import { isEmpty } from 'lodash-es';

interface UsePatientAddressProps {
  formData: OrderAssistFormData;
  patientAddress?: AddressWithFull;
  onFormDataChange: (input: Partial<OrderAssistFormData>) => void;
  useGeoCoordinatesOnly: boolean;
}

// Simple cache for coordinates only
const coordinatesCache: { [key: string]: { latitude: number; longitude: number } } = {};

const createCacheKey = (address: AddressWithFull): string => {
  return `${address.address1}-${address.city}-${address.state}-${address.zipCode}`.toLowerCase();
};

const getCachedCoordinates = (address: AddressWithFull) => {
  const cacheKey = createCacheKey(address);
  return coordinatesCache[cacheKey] || null;
};

const setCachedCoordinates = (
  address: AddressWithFull,
  coordinates: { latitude: number; longitude: number },
) => {
  const cacheKey = createCacheKey(address);
  coordinatesCache[cacheKey] = coordinates;
};

const shouldFetchFromGeoService = (
  address: AddressWithFull,
  useGeoCoordinatesOnly: boolean,
): boolean => {
  if (useGeoCoordinatesOnly) {
    return !address?.coordinates;
  }

  const { address1, city, state, zipCode } = address;
  return !!address1 && !!city && !!state && !zipCode;
};

export const usePatientAddress = ({
  formData,
  patientAddress,
  onFormDataChange,
  useGeoCoordinatesOnly,
}: UsePatientAddressProps): void => {
  useEffect(() => {
    const initAddress = async () => {
      if (patientAddress && isEmpty(formData)) {
        // only coordinates are cached
        if (useGeoCoordinatesOnly) {
          const cachedCoordinates = getCachedCoordinates(patientAddress);
          if (cachedCoordinates) {
            onFormDataChange({
              address: {
                ...patientAddress,
                coordinates: cachedCoordinates,
              },
            });
            return;
          }
        }

        if (shouldFetchFromGeoService(patientAddress, useGeoCoordinatesOnly)) {
          try {
            const result = await logic.fetchGeo(patientAddress);
            if (result.coordinates) {
              setCachedCoordinates(patientAddress, result.coordinates);
            }
            onFormDataChange({
              address: {
                ...patientAddress,
                zipCode: result.zipCode,
                coordinates: result.coordinates,
              },
            });
            return;
          } catch {
            onFormDataChange({ address: patientAddress });
            return;
          }
        }
        onFormDataChange({ address: patientAddress });
      }
    };

    initAddress();
  }, [formData, onFormDataChange, patientAddress, useGeoCoordinatesOnly]);
};
